import { Map as ImmutableMap, Record, type RecordOf } from 'immutable';

import { type Filter, type FilterProps, mkFilter } from '../filters/filtersTypes';

export const SPENDING_WATCH_LIST_REDUCER_KEY = 'spendingWatchListStore';

export type WatchlistPeriod = 'MONTH' | 'FIXED';

// Records are also created optimistically and updated with sparse patches. Server-derived
// amounts and dates can be absent or explicitly null; neither means a calculated zero.
export type SpendingWatchlistProps = {
  id?: string | null;
  clientId?: string | null;
  isDeleted?: boolean | null;
  name?: string;
  filterId?: string;
  thisMonth?: number | null;
  yearToDate?: number | null;
  monthlyTrend?: number | null;
  numMonthlyTrendMonths?: number | null;
  monthSpendingProjection?: number | null;
  targetAmount?: number | null;
  resetTargetAmount?: boolean | null;
  emoji?: string;
  createdAt?: string;
  // Legacy cached field: it is neither populated nor interpreted by this domain.
  monthlyCalculations?: unknown;
  period?: WatchlistPeriod | null;
  startDate?: string | null;
  endDate?: string | null;
  spentSoFar?: number | null;
};

export type SpendingWatchlist = RecordOf<SpendingWatchlistProps>;
export type SavedSpendingWatchlist = SpendingWatchlist & { readonly id: string };

export function isSavedSpendingWatchlist(
  watchlist: SpendingWatchlist | undefined,
): watchlist is SavedSpendingWatchlist {
  return typeof watchlist?.id === 'string' && watchlist.id.length > 0;
}

// The list selector exposes only records with a server id. Other fields retain their
// actual optionality, including clientId, which is omitted from the display projection.
export type Watchlist = Omit<SpendingWatchlistProps, 'id'> & { id: SavedSpendingWatchlist['id']; order?: number };

export type SpendingWatchlistStoreProps = {
  resourcesById: ImmutableMap<string, SpendingWatchlist>;
  lastSyncDate: string | null;
  loadPending: boolean;
  isLoading: boolean;
  error: unknown;
};
export type SpendingWatchlistStore = RecordOf<SpendingWatchlistStoreProps>;

export const mkSpendingWatchListStore = Record<SpendingWatchlistStoreProps>({
  resourcesById: ImmutableMap(),
  lastSyncDate: null,
  loadPending: true,
  isLoading: false,
  error: null,
});

const spendingWatchlistRecord = Record<SpendingWatchlistProps>({
  id: undefined,
  clientId: undefined,
  isDeleted: undefined,
  name: undefined,
  filterId: undefined,
  thisMonth: undefined,
  yearToDate: undefined,
  monthlyTrend: undefined,
  numMonthlyTrendMonths: undefined,
  monthSpendingProjection: undefined,
  targetAmount: undefined,
  resetTargetAmount: undefined,
  emoji: undefined,
  createdAt: undefined,
  monthlyCalculations: undefined,
  period: undefined,
  startDate: undefined,
  endDate: undefined,
  spentSoFar: undefined,
});

// An empty literal is not a server id, matching isSavedSpendingWatchlist.
type ServerId<Id extends string> = Id extends '' ? never : Id;

// Preserve known identities when constructing a record, without claiming that every
// empty record or partial server response already has one.
export function mkSpendingWatchList<Id extends string>(
  props: SpendingWatchlistProps & { id: ServerId<Id> },
): SavedSpendingWatchlist;
export function mkSpendingWatchList(
  props: SpendingWatchlistProps & { clientId: string },
): SpendingWatchlist & { readonly clientId: string };
export function mkSpendingWatchList(props?: SpendingWatchlistProps): SpendingWatchlist;
export function mkSpendingWatchList(props?: SpendingWatchlistProps): SpendingWatchlist {
  return spendingWatchlistRecord(props);
}

export type WatchlistCreate = SpendingWatchlistRequest & ({ id: string } | { clientId: string });
export type WatchlistUpdate = SpendingWatchlistRequest & { id: string };

export type CreateWatchlistInputProps = {
  title: string;
  filterItems: Filter;
  targetAmount?: number | null;
  emoji?: string;
  period?: WatchlistPeriod;
  startDate?: string | null;
  endDate?: string | null;
};
export type CreateWatchlistInput = RecordOf<CreateWatchlistInputProps>;
const createWatchlistInputRecord = Record<CreateWatchlistInputProps>({
  title: '',
  filterItems: mkFilter(),
  targetAmount: undefined,
  emoji: undefined,
  period: undefined,
  startDate: undefined,
  endDate: undefined,
});

export function mkCreateWatchlistInput(input: CreateWatchlistInputProps): CreateWatchlistInput {
  return createWatchlistInputRecord(input);
}

export type DeleteWatchlistInput = {
  filterToDelete?: FilterProps;
  spendingWatchListToDelete: WatchlistUpdate;
};

// Transport and mutation fields are explicit, so calculated state is not sent back to QCS.
export type SpendingWatchlistRequest = Pick<
  SpendingWatchlistProps,
  | 'id'
  | 'clientId'
  | 'isDeleted'
  | 'name'
  | 'filterId'
  | 'targetAmount'
  | 'resetTargetAmount'
  | 'emoji'
  | 'period'
  | 'startDate'
  | 'endDate'
>;
export type SpendingWatchlistResponse = {
  data?: { resources?: SpendingWatchlistProps[] };
};

export type WatchlistSortOrder =
  | 'BY_DATE'
  | 'BY_DATE_ASC'
  | 'BY_ALPHABET'
  | 'BY_ALPHABET_DESC'
  | 'BY_ENDING_SOONEST'
  | 'BY_ENDING_LATEST'
  | 'BY_AMOUNT_ASC'
  | 'BY_AMOUNT_DESC';

export type WatchlistFixedMetrics = {
  isEnded: boolean;
  isActive: boolean;
  daysLeft: number | null;
  totalDays: number;
  totalMonths: number;
  daysElapsed: number;
  spentAbs: number | null;
  timeStep: 'days' | 'months';
  dailyAverage: number | null;
  monthlyAverage: number | null;
  projected: number | null;
  remaining: number | null;
};

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [SPENDING_WATCH_LIST_REDUCER_KEY]: SpendingWatchlistStore;
  }
}
